package top.wcpe.allincore.ui.web.asset

internal object AssetContentPolicy {
    fun validate(request: AssetContentRequest, jpegChecker: JpegDecodeChecker? = null): AssetContentDescriptor {
        val content = identifyContent(request.canonicalPath, request.bytes)
        validateMime(request, content)
        if (content == AssetContent.JPEG) validateJpeg(request.bytes, jpegChecker)
        return AssetContentDescriptor(content.mime, content.budgetClass, request.bytes.size.toLong())
    }

    private fun identifyContent(path: String, bytes: ByteArray): AssetContent {
        val lowerPath = path.lowercase()
        if (hasAicrMagic(bytes)) {
            if (hasConflictingAicrExtension(lowerPath)) failContent("AICR magic 与资产扩展名冲突")
            return AssetContent.AICR
        }
        return when {
            lowerPath.endsWith(".allin") -> strictText(bytes, AssetContent.ALLIN)
            lowerPath.endsWith(".bbmodel") -> strictText(bytes, AssetContent.BBMODEL)
            lowerPath.endsWith(".mock.json") || lowerPath.endsWith(".json") -> strictText(bytes, AssetContent.JSON)
            lowerPath.endsWith(".png") -> requireMagic(hasPngMagic(bytes), AssetContent.PNG)
            lowerPath.endsWith(
                ".jpg",
            ) ||
                lowerPath.endsWith(".jpeg") -> requireMagic(hasJpegMagic(bytes), AssetContent.JPEG)
            lowerPath.endsWith(".webp") -> requireMagic(hasWebpMagic(bytes), AssetContent.WEBP)
            else -> failContent("资产扩展名不在白名单")
        }
    }

    private fun validateMime(request: AssetContentRequest, content: AssetContent) {
        val response = normalizeResponseMime(request.responseMime)
        if (response == null && request.source in setOf(AssetSource.URL, AssetSource.PAGES)) {
            failContent("URL 或 Pages 资产响应 MIME 不能为空")
        }
        if (response != null && response !in content.aliases) failContent("响应 MIME 与资产内容冲突")
        request.expectedMime?.let { expected ->
            val normalized = normalizeRequiredMime(expected)
            if (normalized !in content.aliases) failContent("调用方 expectedMime 与资产内容冲突")
        }
    }

    private fun validateJpeg(bytes: ByteArray, checker: JpegDecodeChecker?) {
        val completeChecker = checker ?: failContent("JPEG 缺少完整解码校验器")
        if (!completeChecker.isComplete(bytes)) failContent("JPEG 未通过完整解码校验")
    }

    private fun strictText(bytes: ByteArray, content: AssetContent): AssetContent {
        if (!isStrictUtf8(bytes)) failContent("文本资产不是严格 UTF-8")
        return content
    }

    private fun requireMagic(matches: Boolean, content: AssetContent): AssetContent {
        if (!matches) failContent("资产 magic 与扩展名冲突")
        return content
    }
}

private enum class AssetContent(val mime: String, val aliases: Set<String>, val budgetClass: AssetBudgetClass) {
    // 静态托管（Python SimpleHTTP / GitHub Pages 等）对未知扩展常给 application/octet-stream；
    // .allin 仍靠扩展名 + 严格 UTF-8 识别，允许 octet-stream 避免假冲突。
    ALLIN(
        "text/plain",
        setOf("text/plain", "application/x-allin", "application/octet-stream"),
        AssetBudgetClass.TEXT,
    ),
    AICR(
        "application/x-allincore-ir",
        setOf("application/x-allincore-ir", "application/octet-stream"),
        AssetBudgetClass.AICR,
    ),
    JSON("application/json", setOf("application/json", "text/json"), AssetBudgetClass.TEXT),
    // 静态托管对 .bbmodel 常给 application/octet-stream；仍靠扩展名 + 严格 UTF-8 识别
    BBMODEL(
        "application/json",
        setOf("application/json", "text/json", "application/octet-stream"),
        AssetBudgetClass.BBMODEL,
    ),
    PNG("image/png", setOf("image/png"), AssetBudgetClass.IMAGE),
    JPEG("image/jpeg", setOf("image/jpeg", "image/jpg"), AssetBudgetClass.IMAGE),
    WEBP("image/webp", setOf("image/webp"), AssetBudgetClass.IMAGE),
}

private fun hasConflictingAicrExtension(path: String): Boolean = listOf(
    ".json",
    ".bbmodel",
    ".png",
    ".jpg",
    ".jpeg",
    ".webp",
    ".svg",
    ".html",
    ".htm",
    ".js",
).any(path::endsWith)

private fun normalizeResponseMime(mime: String?): String? {
    if (mime == null || mime.isBlank()) return null
    return normalizeRequiredMime(mime)
}

private fun normalizeRequiredMime(mime: String): String {
    val normalized = mime.substringBefore(';').trim().lowercase()
    if (normalized.isEmpty()) failContent("MIME 不能为空")
    return normalized
}

internal const val AICR_MAGIC_BYTES = 4

internal fun hasAicrMagic(bytes: ByteArray): Boolean = bytes.matchesPrefix(0x41, 0x49, 0x43, 0x52)

private fun hasPngMagic(bytes: ByteArray): Boolean = bytes.matchesPrefix(0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A)

private fun hasJpegMagic(bytes: ByteArray): Boolean = bytes.matchesPrefix(0xFF, 0xD8, 0xFF)

private fun hasWebpMagic(bytes: ByteArray): Boolean =
    bytes.matchesPrefix(0x52, 0x49, 0x46, 0x46) && bytes.matchesAt(8, 0x57, 0x45, 0x42, 0x50)

private fun ByteArray.matchesPrefix(vararg expected: Int): Boolean = matchesAt(0, *expected)

private fun ByteArray.matchesAt(offset: Int, vararg expected: Int): Boolean {
    if (size < offset + expected.size) return false
    return expected.indices.all { index -> this[offset + index].toInt() and 0xFF == expected[index] }
}

private fun isStrictUtf8(bytes: ByteArray): Boolean {
    var index = 0
    while (index < bytes.size) {
        val first = bytes[index].toInt() and 0xFF
        val length = utf8SequenceLength(bytes, index, first) ?: return false
        index += length
    }
    return true
}

private fun utf8SequenceLength(bytes: ByteArray, index: Int, first: Int): Int? = when {
    first <= 0x7F -> 1
    first in 0xC2..0xDF && bytes.hasContinuation(index + 1) -> 2
    first == 0xE0 && bytes.inRange(index + 1, 0xA0, 0xBF) && bytes.hasContinuation(index + 2) -> 3
    first in 0xE1..0xEC && bytes.hasTwoContinuations(index + 1) -> 3
    first == 0xED && bytes.inRange(index + 1, 0x80, 0x9F) && bytes.hasContinuation(index + 2) -> 3
    first in 0xEE..0xEF && bytes.hasTwoContinuations(index + 1) -> 3
    first == 0xF0 && bytes.inRange(index + 1, 0x90, 0xBF) && bytes.hasTwoContinuations(index + 2) -> 4
    first in 0xF1..0xF3 && bytes.hasThreeContinuations(index + 1) -> 4
    first == 0xF4 && bytes.inRange(index + 1, 0x80, 0x8F) && bytes.hasTwoContinuations(index + 2) -> 4
    else -> null
}

private fun ByteArray.hasContinuation(index: Int): Boolean = inRange(index, 0x80, 0xBF)

private fun ByteArray.hasTwoContinuations(index: Int): Boolean = hasContinuation(index) && hasContinuation(index + 1)

private fun ByteArray.hasThreeContinuations(index: Int): Boolean =
    hasContinuation(index) && hasContinuation(index + 1) && hasContinuation(index + 2)

private fun ByteArray.inRange(index: Int, minimum: Int, maximum: Int): Boolean =
    index < size && (this[index].toInt() and 0xFF) in minimum..maximum

private fun failContent(message: String): Nothing = throw AssetPolicyException(message)
